// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT
import { Component, Input, OnInit } from '@angular/core';
import { downloadCSVFile } from '@lfx/core/helper';
import { EmployeeSubscriptionStats, Company } from '@lfx/core/models';
import { CompanyService } from '@lfx/core/services';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

@Component({
  selector: 'lfx-profile-snapshot-employee-subscriptions',
  templateUrl: './profile-snapshot-employee-subscriptions.component.html',
  styleUrls: ['./profile-snapshot-employee-subscriptions.component.scss'],
  standalone: false,
})
export class ProfileSnapshotEmployeeSubscriptionsComponent implements OnInit {
  @Input() timeRange$: Observable<string>;
  @Input() userId: string;
  searchValue = '';
  _searchValue = new BehaviorSubject<string>('');
  searchValue$ = this._searchValue.asObservable();
  collectionSize = 0;
  currentPage = 1;
  pageSize = 10;
  _page = new BehaviorSubject<number>(1);
  page$ = this._page.asObservable();
  listData$: Observable<any[]>;
  pageData$: Observable<any[]>;
  isLoading = true;
  myCompany$: Observable<Company>;
  columns = [
    {
      name: 'Project Name',
      prop: 'projectName',
      clickableText: false,
      linkableText: false,
      dataStyle: {
        fontWeight: '400',
        fontSize: '14px',
        // color: '#009ADE',
      },
    },
    {
      name: 'Messages',
      prop: 'messageCount',
      clickableText: false,
      // sortable: true,
      sortDir: 'desc',
      dataStyle: {
        // color: '#009ADE',
      },
    },
    {
      name: 'Last Message',
      prop: 'lastMessage',
      clickableText: false,
      // sortable: true,
      sortDir: 'desc',
      dataStyle: {
        // color: '#009ADE',
      },
    },
  ];

  constructor(private companyService: CompanyService) {
    this.myCompany$ = this.companyService.getMyCompany();
  }

  ngOnInit(): void {
    this.listData$ = combineLatest([
      this.myCompany$,
      this.timeRange$.pipe(
        tap(() => {
          this._page.next(1);
          this.currentPage = 1;
        })
      ),
      this.searchValue$,
    ]).pipe(
      tap(() => {
        this.isLoading = true;
      }),
      switchMap(([company, timeRange, searchValue]) =>
        this.companyService
          .getEmployeeSubscriptions(
            this.userId,
            company.id,
            this.companyService.isAllOrgsRollup(),
            timeRange
          )
          .pipe(
            map(data => this.transformData(data)),
            map(data => this.filterData(data, searchValue)),
            tap(data => {
              this.collectionSize = data.length;
            })
          )
      ),
      tap(() => (this.isLoading = false))
    );

    this.pageData$ = combineLatest([this.listData$, this.page$]).pipe(
      map(([data, page]) => {
        const startIndex = (page - 1) * this.pageSize;
        const endIndex = startIndex + this.pageSize;

        return data.slice(startIndex, endIndex);
      })
    );
  }

  private transformData(data: EmployeeSubscriptionStats[]): any[] {
    return data.map(item => ({
      messageCount: this.formatText(item.messageCount),
      projectName: this.formatText(item.projectName),
      lastMessage: this.formatDate(item.lastMessage),
    }));
  }

  formatDate(dateString: string) {
    const formattedDate = this.tsToDate(dateString);

    return this.formatText(formattedDate);
  }

  tsToDate(dateString: string) {
    const date = new Date(dateString);
    const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
    const day = date.getUTCDate().toString().padStart(2, '0');
    const year = date.getUTCFullYear();

    return `${month}/${day}/${year}`;
  }

  formatText(text, subtext = null, url = null) {
    return {
      text,
      subtext,
      url,
    };
  }

  onPageChange(event: any) {
    this._page.next(event);
  }

  private filterData(data: any[], searchValue: string): any[] {
    return data.filter(i =>
      i.projectName.text.toLowerCase().includes(searchValue.toLowerCase())
    );
  }

  clearSearch() {
    this.searchValue = '';
    this.searchInputChange('');
  }

  searchInputChange(value: string): void {
    this._page.next(1);
    this.currentPage = 1;
    this._searchValue.next(value);
  }

  exportData() {
    const subscription = this.listData$.subscribe(data => {
      downloadCSVFile(
        data.map(item => ({
          'Project Name': item.projectName.text,
          'Message Count': item.messageCount.text,
          'Last Message': item.lastMessage.text,
        })),
        'Employee Subscriptions'
      );
      subscription.unsubscribe();
    });
  }
}
